import { useMemo, useState } from 'react';

/**
 * Server-side filter vocabulary for the admin event-log endpoints
 * (`GET /admin/trip/:tripId/events`, `GET /admin/ride/:rideId/events`).
 *
 * The backend (hitch-services #3117) returns 400 for unknown values, so the UI
 * must only ever send values from these lists.
 */
export const EVENT_CATEGORIES = [
  { value: 'rider_action', label: 'Rider action' },
  { value: 'driver_action', label: 'Driver action' },
  { value: 'admin_action', label: 'Admin action' },
  { value: 'optimizer', label: 'Optimizer' },
  { value: 'lifecycle', label: 'Lifecycle' },
  { value: 'engagement', label: 'Engagement' },
];

export const EVENT_ACTORS = [
  { value: 'system', label: 'System' },
  { value: 'driver', label: 'Driver' },
  { value: 'rider', label: 'Rider' },
  { value: 'ops', label: 'Ops' },
];

/**
 * Maps an event `category` to a MUI Chip color so the timeline can color-code
 * the machine-type chip. Unknown/absent categories fall back to `default`.
 */
export const categoryChipColor = (category) =>
  ({
    rider_action: 'info',
    driver_action: 'primary',
    admin_action: 'secondary',
    optimizer: 'success',
    lifecycle: 'default',
    engagement: 'warning',
  })[category] || 'default';

/**
 * Adds `value` to a multi-select list if absent, or removes it if present.
 * Returns a new array (never mutates the input).
 */
export const toggleValue = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

/**
 * Builds the query-string suffix (including leading `?`) for the event-log
 * endpoints from the current filter selection. Empty selections are omitted so
 * the backend applies its own defaults; `includeInternal` is only sent when on
 * (the backend defaults it to false / the ops-visibility floor).
 *
 * @param {{ categories?: string[], actors?: string[], includeInternal?: boolean }} filters
 * @returns {string} e.g. "?categories=lifecycle,optimizer&includeInternal=true" or ""
 */
export const buildEventLogQuery = ({ categories = [], actors = [], includeInternal = false } = {}) => {
  // The values are a fixed snake_case vocabulary with no characters that need
  // escaping, so we join with raw commas (matching the backend's documented
  // comma-separated contract and the existing IsochroneApi query convention)
  // rather than URLSearchParams, which would percent-encode the separators.
  const parts = [];
  if (categories.length) parts.push(`categories=${categories.join(',')}`);
  if (actors.length) parts.push(`actors=${actors.join(',')}`);
  if (includeInternal) parts.push('includeInternal=true');
  return parts.length ? `?${parts.join('&')}` : '';
};

/**
 * Owns the server-side event-log filter state and derives the query string used
 * to fetch. Shared by the ride detail page and the trip dialog so both wire the
 * same params. Empty `categories`/`actors` means "all" (no param sent).
 */
export const useEventLogFilters = () => {
  const [categories, setCategories] = useState([]);
  const [actors, setActors] = useState([]);
  const [includeInternal, setIncludeInternal] = useState(false);

  const queryString = useMemo(
    () => buildEventLogQuery({ categories, actors, includeInternal }),
    [categories, actors, includeInternal],
  );

  return {
    categories,
    setCategories,
    actors,
    setActors,
    includeInternal,
    setIncludeInternal,
    queryString,
  };
};
